import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowLeft,
  faCog,
  faFileAlt,
  faMobileAlt,
  faPhoneAlt,
  faSearch,
  faSpinner,
  faTicketAlt,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Nav,
  Row
} from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import PreviewTicket from 'components/app/support-desk/tickets-preview/PreviewTicket';
import SearchPreferencesModal from 'components/navbar/top/SearchPreferencesModal';
import Pagination from 'components/pagination';
import {
  getPriorityProps,
  getStatusColor
} from 'helpers/utils';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useSearchSettings from 'hooks/useSearchSettings';
import { PrioritySelect } from 'hooks/useTicketsTable';
import GlobalSearchService from 'services/GlobalSearch.service';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formatDateTime = useFormatDateTime();

  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('tickets');
  const [selectedScope, setSelectedScope] = useState('all');
  const [ticketsData, setTicketsData] = useState(null);
  const [contactsData, setContactsData] = useState(null);
  const [articlesData, setArticlesData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [previewTicketId, setPreviewTicketId] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    orderBy: null,
    direction: 'desc'
  });

  const { searchSettings, updateSearchSettings } = useSearchSettings();
  const MIN_SEARCH_LENGTH = 3;

  // Opções de ordenação por tipo
  const getSortOptions = () => {
    switch (activeTab) {
      case 'contacts':
        return [
          { value: 'name', label: 'Nome' },
          { value: 'last_update_date', label: 'Última atualização' }
        ];
      case 'tickets':
      case 'articles':
      default:
        return [
          { value: 'creation_date', label: 'Data de criação' },
          { value: 'last_update_date', label: 'Última atualização' }
        ];
    }
  };

  // Inicializar dados da URL
  useEffect(() => {
    const query = searchParams.get('q');
    const scope = searchParams.get('scope') || 'tickets';

    if (query) {
      setSearchTerm(query);
      setSelectedScope(scope);
      setActiveTab(scope === 'all' ? 'tickets' : scope);
    }
  }, [searchParams]);

  // Carregar dados quando mudar termo, aba ou filtros
  const loadData = useCallback(async () => {
    if (
      !searchTerm ||
      searchTerm.length < MIN_SEARCH_LENGTH ||
      !searchSettings
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const searchBody = {
        query: searchTerm.trim(),
        searchIn: {
          searchInSubject: searchSettings.searchInSubject,
          searchInDescription: searchSettings.searchInDescription,
          searchInProperties: searchSettings.searchInProperties,
          searchInNotes: searchSettings.searchInNotes,
          searchInAttachments: searchSettings.searchInAttachments
        },
        page: filters.page,
        size: filters.size
      };

      // Adicionar ordenação apenas se foi selecionada
      if (filters.orderBy) {
        searchBody.orderBy = filters.orderBy;
        searchBody.direction = filters.direction;
      }

      // Carregar dados da aba ativa
      switch (activeTab) {
        case 'tickets':
          const tickets = await GlobalSearchService.searchTickets(searchBody);
          setTicketsData(tickets);
          break;
        case 'contacts':
          const contacts = await GlobalSearchService.searchContacts(searchBody);
          setContactsData(contacts);
          break;
        case 'articles':
          const articles = await GlobalSearchService.searchArticles(searchBody);
          setArticlesData(articles);
          break;
      }

      // Se for primeira carga e veio do escopo 'all', carregar contadores apenas se não tiver dados
      if (
        selectedScope === 'all' &&
        filters.page === 0 &&
        !ticketsData &&
        !contactsData &&
        !articlesData
      ) {
        const countBody = { ...searchBody, size: 20 };

        const [ticketsCount, contactsCount, articlesCount] = await Promise.all([
          GlobalSearchService.searchTickets(countBody),
          GlobalSearchService.searchContacts(countBody),
          GlobalSearchService.searchArticles(countBody)
        ]);

        setTicketsData(ticketsCount);
        setContactsData(contactsCount);
        setArticlesData(articlesCount);
      }
    } catch (err) {
      console.error('Erro na busca:', err);
      setError('Erro ao realizar a busca. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, activeTab, filters, searchSettings, selectedScope]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearchSubmit = e => {
    e.preventDefault();
    if (searchTerm.trim().length >= MIN_SEARCH_LENGTH) {
      setFilters({
        page: 0,
        size: 20,
        orderBy: null,
        direction: 'desc'
      });
      // Limpar dados para forçar nova busca
      setTicketsData(null);
      setContactsData(null);
      setArticlesData(null);
    }
  };

  const handleTabChange = tab => {
    setActiveTab(tab);
    setFilters({ page: 0, size: 20, orderBy: null, direction: 'desc' });
  };

  const handleScopeChange = scope => {
    setSelectedScope(scope);
    setActiveTab(scope === 'all' ? 'tickets' : scope);
    setFilters({ page: 0, size: 20, orderBy: null, direction: 'desc' });
  };

  const handlePreferencesClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setShowPreferencesModal(true);
  };

  const handlePreferencesUpdate = async newSettings => {
    const result = await updateSearchSettings(newSettings);
    if (result.success) {
      // Refazer a busca com as novas configurações
      setFilters(prev => ({ ...prev, page: 0 }));
      // Limpar dados para forçar nova busca
      setTicketsData(null);
      setContactsData(null);
      setArticlesData(null);
    }
    return result;
  };

  const handleSortChange = orderBy => {
    setFilters(prev => ({ ...prev, orderBy, page: 0 }));
  };

  const handleDirectionChange = direction => {
    setFilters(prev => ({ ...prev, direction, page: 0 }));
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'tickets':
        return ticketsData;
      case 'contacts':
        return contactsData;
      case 'articles':
        return articlesData;
      default:
        return null;
    }
  };

  const getTabCount = type => {
    switch (type) {
      case 'tickets':
        return ticketsData?.totalElements || 0;
      case 'contacts':
        return contactsData?.totalElements || 0;
      case 'articles':
        return articlesData?.totalElements || 0;
      default:
        return 0;
    }
  };

  const getSortLabel = () => {
    const sortOptions = getSortOptions();
    const currentOption = sortOptions.find(
      option => option.value === filters.orderBy
    );
    return currentOption ? currentOption.label : 'Sem ordenação';
  };

  const renderTicketCard = ticket => {
    const getStatusBadge = status => {
      switch (status) {
        case 'TO_DO':
          return { bg: getStatusColor(status), text: 'A Fazer' };
        case 'IN_PROGRESS':
          return { bg: getStatusColor(status), text: 'Atendendo' };
        case 'PENDING':
          return { bg: getStatusColor(status), text: 'Pausado' };
        case 'COMPLETED':
          return { bg: getStatusColor(status), text: 'Concluído' };
        default:
          return { bg: getStatusColor(status), text: 'Fechado' };
      }
    };

    const status = getStatusBadge(ticket.status);
    const priority = getPriorityProps(ticket.priority);

    return (
      <Card key={ticket.id} className="mb-3 shadow-sm p-4">
        <Card.Body className="p-3">
          <div className="d-flex align-items-start">
            <div className="flex-grow-1">
              <div className="d-flex align-items-center mb-2">
                <SoftBadge bg="light" style={{ backgroundColor: status.bg }}>
                  {status.text}
                </SoftBadge>

                <PrioritySelect
                  title={priority.text}
                  color={priority.bg}
                  data={priority.percentage}
                  className="ms-2"
                />
              </div>
              <div className="d-flex align-items-center mb-2">
                <p className="m-0 fs-9 text-truncate">
                  <Link
                    to={`/tickets/${ticket.id}`}
                    className="text-decoration-none"
                    onMouseEnter={e => {
                      setPreviewTicketId(ticket.id);
                      setHoveredElement(e.currentTarget);
                    }}
                    onMouseLeave={() => setPreviewTicketId(null)}
                  >
                    #{ticket.code} {ticket.subject}
                  </Link>
                  {previewTicketId === ticket.id && (
                    <PreviewTicket
                      hoveredElement={hoveredElement}
                      data={{
                        subject: ticket.subject,
                        code: ticket.code,
                        id: ticket.id,
                        status: ticket.status,
                        description: ticket.description
                      }}
                    />
                  )}
                </p>
              </div>

              <div className="text-muted d-flex align-items-center fs-10">
                <FontAwesomeIcon icon={faUser} className="me-1" />
                De:{' '}
                {`${ticket?.contactCustomer.name} (${ticket.customer?.name})`}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderContactCard = contact => {
    return (
      <Card key={contact.id} className="mb-3 shadow-sm border-0">
        <Card.Body className="p-3">
          <div className="d-flex align-items-center">
            <div className="me-4">
              <Avatar
                size="3xl"
                name={contact.name}
                mediaClass="fs-0 fw-semi-bold"
              />
            </div>

            <div className="flex-grow-1">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0 fw-semi-bold">
                  <span className="text-decoration-none text-body">
                    {contact.name}
                  </span>
                </h5>
                <div className="d-flex gap-2">
                  {contact.email && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      href={`mailto:${contact.email}`}
                      className="btn-icon"
                    >
                      <FontAwesomeIcon icon={faEnvelope} />
                    </Button>
                  )}
                  {contact.phone && (
                    <Button
                      variant="outline-success"
                      size="sm"
                      href={`tel:${contact.phone}`}
                      className="btn-icon"
                    >
                      <FontAwesomeIcon icon={faMobileAlt} />
                    </Button>
                  )}
                  {contact.landline && (
                    <Button
                      variant="outline-info"
                      size="sm"
                      href={`tel:${contact.landline}`}
                      className="btn-icon"
                    >
                      <FontAwesomeIcon icon={faPhoneAlt} />
                    </Button>
                  )}
                </div>
              </div>

              <div className="row g-3">
                {contact.email && (
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="me-2 text-primary"
                        size="sm"
                      />
                      <span className="text-body-secondary small">
                        {contact.email}
                      </span>
                    </div>
                  </div>
                )}
                {contact.phone && (
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={faMobileAlt}
                        className="me-2 text-success"
                        size="sm"
                      />
                      <span className="text-body-secondary small">
                        {contact.phone}
                      </span>
                    </div>
                  </div>
                )}
                {contact.landline && (
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={faPhoneAlt}
                        className="me-2 text-info"
                        size="sm"
                      />
                      <span className="text-body-secondary small">
                        {contact.landline}
                      </span>
                    </div>
                  </div>
                )}
                {contact.lastUpdateDate && (
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="me-2 text-warning"
                        size="sm"
                      />
                      <span className="text-body-secondary small">
                        Atualizado: {formatDateTime(contact.lastUpdateDate)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderArticleCard = article => {
    return (
      <Card key={article.id} className="mb-3 shadow-sm">
        <Card.Body className="p-3">
          <div className="d-flex align-items-start">
            <FontAwesomeIcon
              size="lg"
              icon={faFileAlt}
              className="me-3 my-auto"
            />
            <div>
              <p className="m-0 fs-9">
                <Link
                  to={`/documents/articles/${article.id}`}
                  className="text-decoration-none"
                >
                  {article.title}
                </Link>
              </p>
              <span className="text-muted fs-10">{`em ${article?.kbFolder?.kbGroup?.name} > ${article?.kbFolder?.name}`}</span>

              <div className="fs--2 text-500">
                Criado por: {article.user?.firstName} {article.user?.lastName} •{' '}
                {formatDateTime(article.creationDate)} •{' '}
                {article.lastUpdateDate &&
                  `Última atualização: ${formatDateTime(
                    article.lastUpdateDate
                  )}`}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderContent = () => {
    const data = getCurrentData();

    if (isLoading) {
      return (
        <div className="text-center py-5">
          <FontAwesomeIcon
            icon={faSpinner}
            className="text-muted"
            spin
            size="2x"
          />
          <div className="mt-3 text-muted">Carregando resultados...</div>
        </div>
      );
    }

    if (!data || !data.content || data.content.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="text-muted">
            Nenhum{' '}
            {activeTab === 'tickets'
              ? 'ticket'
              : activeTab === 'contacts'
              ? 'contato'
              : 'artigo'}{' '}
            encontrado.
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="mb-3">
          {activeTab === 'tickets' &&
            data.content?.map(ticket => renderTicketCard(ticket))}
          {activeTab === 'contacts' &&
            data.content?.map(contact => renderContactCard(contact))}
          {activeTab === 'articles' &&
            data.content?.map(article => renderArticleCard(article))}
        </div>

        {data && (
          <Pagination data={data} filters={filters} setFilters={setFilters} />
        )}
      </>
    );
  };

  return (
    <Container fluid className="py-3">
      {/* Results */}
      {searchTerm && (
        <>
          {/* Tabs and Scope Selection */}
          <Card className="mb-3">
            <header
              as={Flex}
              direction="column"
              className="p-0 px-4 pt-3"
              style={{ paddingBottom: '11px' }}
            >
              {/* Header */}
              <Row>
                <Col>
                  <div className="d-flex align-items-center mb-3">
                    <Button
                      variant="link"
                      className="text-700 p-0 me-2"
                      onClick={() => navigate(-1)}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>
                    <h4 className="mb-0">Pesquisa</h4>
                  </div>
                </Col>
              </Row>

              {/* Error State */}
              {error && (
                <Alert variant="danger" className="mb-3">
                  <Alert.Heading>Erro na Pesquisa</Alert.Heading>
                  <p className="mb-0">{error}</p>
                </Alert>
              )}
              <div className="d-flex align-items-center justify-content-between">
                <Nav variant="tabs" className="nav-tabs-card">
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === 'tickets'}
                      onClick={() => handleTabChange('tickets')}
                      className="text-nowrap"
                    >
                      <FontAwesomeIcon icon={faTicketAlt} className="me-2" />
                      Tickets
                      <Badge bg="primary" className="ms-2">
                        {getTabCount('tickets')}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === 'contacts'}
                      onClick={() => handleTabChange('contacts')}
                      className="text-nowrap"
                    >
                      <FontAwesomeIcon icon={faUser} className="me-2" />
                      Contatos
                      <Badge bg="info" className="ms-2">
                        {getTabCount('contacts')}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === 'articles'}
                      onClick={() => handleTabChange('articles')}
                      className="text-nowrap"
                    >
                      <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                      Artigos
                      <Badge bg="warning" className="ms-2">
                        {getTabCount('articles')}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                <div className="d-flex align-items-center gap-2">
                  {/* Scope Selection */}
                  <Dropdown size="sm">
                    <Dropdown.Toggle variant="falcon-default" size="sm">
                      Escopo:{' '}
                      {selectedScope === 'all'
                        ? 'Todos'
                        : selectedScope === 'tickets'
                        ? 'Tickets'
                        : selectedScope === 'contacts'
                        ? 'Contatos'
                        : 'Soluções'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleScopeChange('all')}>
                        Todos
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleScopeChange('tickets')}
                      >
                        Tickets
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleScopeChange('contacts')}
                      >
                        Contatos
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleScopeChange('articles')}
                      >
                        Soluções
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Preferences Button */}
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handlePreferencesClick}
                    className="p-1 text-muted border-0 bg-transparent"
                    title="Configurar preferências de pesquisa"
                  >
                    <FontAwesomeIcon icon={faCog} size="sm" />
                  </Button>
                </div>
              </div>
            </header>
          </Card>

          {/* Sort and Actions Bar */}
          {getCurrentData() && getCurrentData().content?.length > 0 && (
            <Row className="g-3 mb-3 align-items-center">
              <Col xs="auto">
                <div className="d-flex align-items-center gap-2">
                  <Dropdown size="sm">
                    <Dropdown.Toggle variant="falcon-default" size="sm">
                      Ordenar por: {getSortLabel()}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleSortChange(null)}>
                        Sem ordenação
                      </Dropdown.Item>
                      {getSortOptions().map(option => (
                        <Dropdown.Item
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                        >
                          {option.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  <Dropdown size="sm">
                    <Dropdown.Toggle variant="falcon-default" size="sm">
                      {filters.direction === 'asc'
                        ? 'Crescente'
                        : 'Decrescente'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={() => handleDirectionChange('asc')}
                      >
                        Crescente
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDirectionChange('desc')}
                      >
                        Decrescente
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Col>
            </Row>
          )}

          {/* Results Content */}
          {renderContent()}
        </>
      )}

      {/* No Search Term */}
      {!searchTerm && (
        <Card>
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon
              icon={faSearch}
              size="3x"
              className="text-muted mb-3"
            />
            <h5>Digite um termo para pesquisar</h5>
            <p className="text-muted">
              Use a barra de pesquisa acima para encontrar tickets, contatos e
              soluções.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Modal de preferências */}
      <SearchPreferencesModal
        show={showPreferencesModal}
        onHide={() => setShowPreferencesModal(false)}
        onUpdate={handlePreferencesUpdate}
      />
    </Container>
  );
};

export default SearchResults;
